import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { ToastContext } from "context/ToastContext";
import { parseDate } from "helpers/date";
import { calculateClearPatientFee } from "helpers/fee";
import { parsePrice, centsToDollars, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { Row } from "primereact/row";
import { useEffect, useMemo, useState, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useUpdateOrderMutation } from "store/queries/order";
import styled from "styled-components";
import { OrderStatus } from "types/Order/OrderStatus";
import { ISplitPaymentDetail, IPaymentPlan } from "types/Payment";

type ProviderAmountRow = {
  providerName: string;
  cptCode: string;
  providerPriority: number;
  adjustedAllowablePrice: number;
  clearDiscountedPrice: number;
  initialClearDiscountedPrice?: number;
  expectedTransferAmount: number;
  clearFeeAmountInCents: number;
  clearPatientFeeAmountInCents?: number;
  // CLE-2798: only on PAID installments
  clearFeeReducedByCreditCardFeeInCents?: number;
  gfeCreditCardFeeAmountInCents?: number;
  patientPortion: number;
  remainingToPayProvider: number;
  providerId: string;
};

const StyledDataTable = styled(DataTable)`
  margin-top: ${sizer(6)};

  tr {
    background: var(--gray-50);
  }

  span.p-column-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--fontsize-contents);
    color: var(--color-black-4);
  }
  .p-rowgroup-header {
    display: none;
  }

  td {
    font-weight: var(--font-weight-medium);
    font-size: var(--fontsize-contents);
    color: var(--color-matte-black);
  }
`;

const SplitPaymentRow = styled.div`
  padding: 4px 0;
  border-bottom: 1px dashed #e0e0e0;
  &:last-child {
    border-bottom: none;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  input {
    max-width: 120px;
  }
`;

// CLE-2798 — cells for a PAID installment on a credit-card charge. Module-level so the table does
// not define components during render. The surcharge is Clear's money; it sits on the provider row
// only to explain that row's reduced fee. On a two-card charge each card shows its own line.
function renderClearFeeWithWaiver(inner: JSX.Element, waivedInCents: number) {
  if (!(waivedInCents > 0)) return inner;
  return (
    <>
      {inner}
      <span
        className="text-gray-500 text-xs ml-1"
        title="Waived because the patient paid the surcharge on this charge"
      >
        (−{parsePrice(centsToDollars(waivedInCents))} surcharge)
      </span>
    </>
  );
}

function ProviderPayoutCalculations({
  providerAmounts,
  clearFee,
  payments = [],
  fullBreakdownSplitPaymentDetails,
  paymentPlanSplitPaymentDetails,
  orderStatus,
  orderId,
  onUpdateOrder,
  clearPatientFeeValueInPercentage,
  paymentPlanSplitAmountsByProvider,
  fullBreakdownSplitAmountsByProvider,
  orderType,
  paymentPlan,
}: {
  providerAmounts?: ProviderAmountRow[];
  clearFee?: number | null;
  payments?: any[];
  fullBreakdownSplitPaymentDetails?: ISplitPaymentDetail[];
  paymentPlanSplitPaymentDetails?: ISplitPaymentDetail[];
  orderStatus: string;
  orderId: string;
  onUpdateOrder: () => void;
  clearPatientFeeValueInPercentage?: number;
  paymentPlanSplitAmountsByProvider?: any;
  fullBreakdownSplitAmountsByProvider?: any;
  orderType: string;
  paymentPlan?: IPaymentPlan;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [edit, setEdit] = useState(false);
  const [difference, setDifference] = useState({});
  const [saveButtonLoading, setSaveButtonLoading] = useState(false);
  const { current: toastElement } = useContext(ToastContext);
  // Sort providerAmounts by providerPriority in ascending order
  const [sortedProviderAmounts, setSortedProviderAmounts] = useState(
    (providerAmounts || [])
      .slice()
      .sort(
        (a: ProviderAmountRow, b: ProviderAmountRow) =>
          a.providerPriority - b.providerPriority
      )
  );
  const [updateOrder] = useUpdateOrderMutation();
  const [rows] = useState(1); // Show one payment at a time
  const { watch } = useFormContext();
  const remainingInstallmentsField = watch("paymentPlanDuration");

  useEffect(() => {
    if (providerAmounts) {
      setSortedProviderAmounts(
        (providerAmounts || [])
          .slice()
          .sort(
            (a: ProviderAmountRow, b: ProviderAmountRow) =>
              a.providerPriority - b.providerPriority
          )
      );
    }
  }, [providerAmounts]);

  const handleResetProviderAmounts = () => {
    setSortedProviderAmounts(
      (providerAmounts || [])
        .slice()
        .sort(
          (a: ProviderAmountRow, b: ProviderAmountRow) =>
            a.providerPriority - b.providerPriority
        )
    );
    setDifference({});
  };

  const handleSaveProviderAmounts = () => {
    const totalPatientPortion = sortedProviderAmounts.reduce(
      (acc, item) => acc + (item.patientPortion || 0),
      0
    );

    // Add clear patient fee if it exists
    let finalTotalPatientPortion = totalPatientPortion;
    if (
      clearPatientFeeValueInPercentage &&
      clearPatientFeeValueInPercentage > 0
    ) {
      // Convert to dollars, calculate fee with rounding, convert back to cents
      const totalInDollars = centsToDollars(totalPatientPortion);
      const clearPatientFeeInDollars = calculateClearPatientFee(
        totalInDollars,
        clearPatientFeeValueInPercentage
      );
      const clearPatientFeeAmount = dollarsToCents(clearPatientFeeInDollars);
      finalTotalPatientPortion = totalPatientPortion + clearPatientFeeAmount;
    }

    setSaveButtonLoading(true);
    updateOrder({
      id: orderId,
      providerPayout: {
        differenceByProvider: difference,
        totalPatientPortion: finalTotalPatientPortion,
      },
    })
      .unwrap()
      .then(() => {
        onUpdateOrder();
        setDifference({});
        toastElement?.show({
          summary: "Success!",
          severity: "success",
          detail: "Your estimate has been updated.",
        });
        setEdit(false);
        setSaveButtonLoading(false);
      })
      .catch(() => {
        setSaveButtonLoading(false);
        toastElement?.show({
          severity: "error",
          detail: "Try again later.",
          summary: "Something went wrong.",
        });
      });
  };

  useEffect(() => {
    // Reset currentPage to 0 whenever Installments count changes
    setCurrentPage(0);
  }, [remainingInstallmentsField]);

  const onPageChange = (event: PaginatorPageState) => {
    setCurrentPage(event.first);
  };

  const firstColumnTemplate = (row: ProviderAmountRow) => {
    return <span className="font-bold">{row.providerName}</span>;
  };

  const cptCodeTemplate = (row: ProviderAmountRow) => {
    return <span>{row.cptCode || "N/A"}</span>;
  };

  const paymentPriorityTemplate = (row: ProviderAmountRow) => {
    return <span>{row.providerPriority}</span>;
  };

  const clearPriceTemplate = (row: ProviderAmountRow) => {
    const { initialClearDiscountedPrice, clearDiscountedPrice } = row;
    const showInitial =
      initialClearDiscountedPrice != null &&
      initialClearDiscountedPrice !== clearDiscountedPrice;
    return (
      <div>
        <span>{parsePrice(centsToDollars(clearDiscountedPrice))}</span>
        {showInitial && (
          <div style={{ fontSize: "0.75rem", color: "var(--color-black-4)" }}>
            Init: {parsePrice(centsToDollars(initialClearDiscountedPrice))}
          </div>
        )}
      </div>
    );
  };

  const totalAllowableTemplate = (row: ProviderAmountRow) => {
    return (
      <span>{parsePrice(centsToDollars(row.adjustedAllowablePrice))}</span>
    );
  };

  const patientPortionTemplate = (
    row: ProviderAmountRow,
    disabled: boolean
  ) => {
    const onValueChange = (value: any) => {
      setSortedProviderAmounts((prev) =>
        prev.map((item) => {
          if (item.providerId === row.providerId) {
            return {
              ...item,
              patientPortion: dollarsToCents(value),
            };
          }
          return item;
        })
      );
      const initialValue = sortedProviderAmounts?.find(
        (item) => item.providerId === row.providerId
      )?.patientPortion;
      const differenceValue =
        initialValue !== undefined ? dollarsToCents(value) - initialValue : 0;
      setDifference((prev) => ({
        ...prev,
        [row.providerId]: differenceValue,
      }));
    };
    return (
      <StyledInputNumber
        value={centsToDollars(row.patientPortion)}
        currency="USD"
        locale="en-US"
        disabled={disabled}
        mode="currency"
        inputId="outOfPocketMax"
        onValueChange={({ value }) => onValueChange(value)}
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
        aria-autocomplete="none"
      />
    );
  };

  const renderAmountWithSplits = (
    providerId: string,
    amountType:
      | "expectedTransferAmount"
      | "clearFeeAmountInCents"
      | "clearPatientFeeAmountInCents"
      | "gfeCreditCardFeeAmountInCents",
    fallbackAmount: number,
    splitAmountsByProvider?: any,
    shouldShowSplits = false
  ) => {
    // If we have splitAmountsByProvider data for this provider and amount type, and we should show splits, render split amounts
    if (
      shouldShowSplits &&
      splitAmountsByProvider &&
      splitAmountsByProvider[providerId] &&
      splitAmountsByProvider[providerId][amountType] &&
      splitAmountsByProvider[providerId][amountType].length > 0
    ) {
      const splitAmounts = splitAmountsByProvider[providerId][amountType];
      return (
        <div>
          {splitAmounts.map((split: any, index: number) => (
            <SplitPaymentRow key={split.paymentId || index.toString()}>
              {parsePrice(centsToDollars(split.amount))}
            </SplitPaymentRow>
          ))}
        </div>
      );
    }

    // Otherwise, show the regular total amount
    return <span>{parsePrice(centsToDollars(fallbackAmount))}</span>;
  };

  // Format clearfee with how many decimal we want to show, this is for development purposes
  const formatClearFee = (amount: number) => {
    return (amount / 100).toFixed(3);
  };

  const installmentColumns = useMemo(() => {
    const baseColumns = [
      {
        field: "providerName",
        header: "Provider",
        body: (row: ProviderAmountRow) => firstColumnTemplate(row),
      },
    ];

    // Add CPT Code and Payment Priority columns only if not Patient Responsibility
    if (orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY) {
      baseColumns.push(
        {
          field: "cptCode",
          header: "CPT Code",
          body: cptCodeTemplate,
        },
        {
          field: "providerPriority",
          header: "Payment Priority",
          body: paymentPriorityTemplate,
        }
      );
    }

    // Add remaining columns
    baseColumns.push(
      {
        field: "clearDiscountedPrice",
        header: "Clear Price",
        body: clearPriceTemplate,
      },
      {
        field: "expectedTransferAmount",
        header: "Actual Payment",
        body: (row: ProviderAmountRow) => {
          return renderAmountWithSplits(
            row.providerId,
            "expectedTransferAmount",
            row.expectedTransferAmount,
            paymentPlanSplitAmountsByProvider,
            currentPage === 0
          );
        },
      },
      {
        field: "clearFeeAmountInCents",
        header: "Clear Fee",
        body: (row: ProviderAmountRow) => {
          // CLE-2798: on an order with a credit-card fee the clear fee of a not-yet-charged
          // installment depends on the card used, so it is shown only once the charge has run
          // (Ani: "only show the fees once we know what happens").
          // CLE-2798: on a PAID installment the fee shown is the real one; the part waived in
          // exchange for the patient's surcharge is noted next to it. Unpaid installments keep the
          // projected clear fee as before.
          return renderClearFeeWithWaiver(
            renderAmountWithSplits(
              row.providerId,
              "clearFeeAmountInCents",
              row.clearFeeAmountInCents,
              paymentPlanSplitAmountsByProvider,
              currentPage === 0
            ),
            row.clearFeeReducedByCreditCardFeeInCents || 0
          );
        },
      },
      ...(sortedProviderAmounts.some(
        (row) =>
          row.clearPatientFeeAmountInCents &&
          row.clearPatientFeeAmountInCents > 0
      )
        ? [
            {
              field: "clearPatientFeeAmountInCents",
              header: "Clear Patient Fee",
              body: (row: ProviderAmountRow) => {
                // Only show split payments for initial deposit (currentPage === 0)
                return renderAmountWithSplits(
                  row.providerId,
                  "clearPatientFeeAmountInCents",
                  row.clearPatientFeeAmountInCents || 0,
                  paymentPlanSplitAmountsByProvider,
                  currentPage === 0
                );
              },
            },
          ]
        : []),
      // CLE-2798: the credit-card surcharge collected on a PAID installment (never projected).
      ...(payments[currentPage]?.providerBreakdown?.some(
        (row: ProviderAmountRow) => (row.gfeCreditCardFeeAmountInCents || 0) > 0
      )
        ? [
            {
              field: "gfeCreditCardFeeAmountInCents",
              header: "Surcharge",
              body: (row: ProviderAmountRow) =>
                renderAmountWithSplits(
                  row.providerId,
                  "gfeCreditCardFeeAmountInCents",
                  row.gfeCreditCardFeeAmountInCents || 0,
                  paymentPlanSplitAmountsByProvider,
                  currentPage === 0
                ),
            },
          ]
        : [])
    );

    return baseColumns;
  }, [
    currentPage,
    payments,
    paymentPlanSplitPaymentDetails,
    sortedProviderAmounts,
    paymentPlanSplitAmountsByProvider,
    orderType,
  ]);

  // Sort payments by providerPriority in ascending order
  const sortedPayments = useMemo(() => {
    return payments.map((payment) => ({
      ...payment,
      providerBreakdown: payment.providerBreakdown
        .slice()
        .sort(
          (a: ProviderAmountRow, b: ProviderAmountRow) =>
            a.providerPriority - b.providerPriority
        ),
    }));
  }, [payments]);

  const fullPaymentColumns = useMemo(() => {
    const baseColumns = [
      {
        field: "providerName",
        header: "Provider",
        body: (row: ProviderAmountRow) => firstColumnTemplate(row),
      },
    ];

    // Add CPT Code and Payment Priority columns only if not Patient Responsibility
    if (orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY) {
      baseColumns.push(
        {
          field: "cptCode",
          header: "CPT Code",
          body: cptCodeTemplate,
        },
        {
          field: "providerPriority",
          header: "Payment Priority",
          body: paymentPriorityTemplate,
        }
      );
    }

    // Add remaining columns
    baseColumns.push(
      {
        field: "adjustedAllowablePrice",
        header: "Total Allowable",
        body: totalAllowableTemplate,
      },
      {
        field: "patientPortion",
        header: "Patient Portion",
        body: (row: ProviderAmountRow) => patientPortionTemplate(row, !edit),
      },
      {
        field: "clearDiscountedPrice",
        header: "Clear Price",
        body: clearPriceTemplate,
      },
      {
        field: "expectedTransferAmount",
        header: "Actual Payment",
        body: (row: ProviderAmountRow) => {
          return renderAmountWithSplits(
            row.providerId,
            "expectedTransferAmount",
            row.expectedTransferAmount,
            fullBreakdownSplitAmountsByProvider,
            true
          );
        },
      },
      {
        field: "clearFeeAmountInCents",
        header: "Clear Fee",
        body: (row: ProviderAmountRow) => {
          // CLE-2798: a PROCESSED credit-card full payment shows the real (reduced) fee with the
          // waived part noted, exactly like a paid installment.
          return renderClearFeeWithWaiver(
            renderAmountWithSplits(
              row.providerId,
              "clearFeeAmountInCents",
              row.clearFeeAmountInCents,
              fullBreakdownSplitAmountsByProvider,
              true
            ),
            row.clearFeeReducedByCreditCardFeeInCents || 0
          );
        },
      },
      ...(sortedProviderAmounts.some(
        (row) =>
          row.clearPatientFeeAmountInCents &&
          row.clearPatientFeeAmountInCents > 0
      )
        ? [
            {
              field: "clearPatientFeeAmountInCents",
              header: "Clear Patient Fee",
              body: (row: ProviderAmountRow) => {
                return renderAmountWithSplits(
                  row.providerId,
                  "clearPatientFeeAmountInCents",
                  row.clearPatientFeeAmountInCents || 0,
                  fullBreakdownSplitAmountsByProvider,
                  true
                );
              },
            },
          ]
        : []),
      // CLE-2798: the surcharge collected on a PROCESSED full payment (never projected).
      ...(sortedProviderAmounts.some(
        (row) => (row.gfeCreditCardFeeAmountInCents || 0) > 0
      )
        ? [
            {
              field: "gfeCreditCardFeeAmountInCents",
              header: "Surcharge",
              body: (row: ProviderAmountRow) =>
                renderAmountWithSplits(
                  row.providerId,
                  "gfeCreditCardFeeAmountInCents",
                  row.gfeCreditCardFeeAmountInCents || 0,
                  fullBreakdownSplitAmountsByProvider,
                  true
                ),
            },
          ]
        : [])
    );

    return baseColumns;
  }, [
    fullBreakdownSplitPaymentDetails,
    edit,
    sortedProviderAmounts,
    fullBreakdownSplitAmountsByProvider,
    orderType,
  ]);

  const isDownPaymentOnly = useMemo(() => {
    return paymentPlan?.isDownPaymentOnly;
  }, [paymentPlan]);

  const firstPageLabel = useMemo(() => {
    return isDownPaymentOnly ? "Down Payment" : "Initial Payment";
  }, [isDownPaymentOnly]);

  const actionHeader = () => {
    return (
      <Button
        icon="pi pi-pencil"
        onClick={() => setEdit(true)}
        className="p-button-outlined-light"
      />
    );
  };

  const actionFooter = () => {
    return (
      <div className="flex gap-1 mt-3">
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={handleSaveProviderAmounts}
          loading={saveButtonLoading}
        />
        <Button
          label="Cancel"
          onClick={() => {
            handleResetProviderAmounts();
            setEdit(false);
          }}
          icon="pi pi-times"
          style={{ marginLeft: "0.5em" }}
          className="p-button-outlined-light"
        />
      </div>
    );
  };

  // One footer for both tables. Clear's take on a charge is clear fee + clear patient fee + surcharge;
  // CLE-2798: with a surcharge in play "Total Clear Fee" alone understated it (the waived fee dollars
  // moved to the surcharge line), so the surcharge and the total to Clear are shown too.
  const feeFooter = (rows: ProviderAmountRow[], columnCount: number) => {
    const totalClearFee = rows.reduce(
      (sum, row) => sum + (row.clearFeeAmountInCents || 0),
      0
    );
    const totalClearPatientFee = rows.reduce(
      (sum, row) => sum + (row.clearPatientFeeAmountInCents || 0),
      0
    );
    const totalSurcharge = rows.reduce(
      (sum, row) => sum + (row.gfeCreditCardFeeAmountInCents || 0),
      0
    );
    const hasClearPatientFee = rows.some(
      (row) => (row.clearPatientFeeAmountInCents || 0) > 0
    );
    const hasSurcharge = totalSurcharge > 0;

    const lines: Array<[string, number]> = [["Total Clear Fee", totalClearFee]];
    if (hasClearPatientFee)
      lines.push(["Total Clear Patient Fee", totalClearPatientFee]);
    if (hasSurcharge) {
      lines.push(["Total Surcharge", totalSurcharge]);
      lines.push([
        "Total to Clear",
        totalClearFee + totalClearPatientFee + totalSurcharge,
      ]);
    }

    // Right-hand block spans the fee columns (clear fee, CPF, surcharge) so the labels line up.
    const valueColSpan =
      1 + (hasClearPatientFee ? 1 : 0) + (hasSurcharge ? 1 : 0);
    const singleLine = lines.length === 1;
    const footerValue = singleLine ? (
      parsePrice(centsToDollars(lines[0][1]))
    ) : (
      <>
        {lines.map(([label, cents], i) => (
          <span key={label} data-testid={`fee-footer-${label}`}>
            {i > 0 && <br />}
            {label} {parsePrice(centsToDollars(cents))}
          </span>
        ))}
      </>
    );

    return (
      <ColumnGroup>
        <Row>
          <Column
            footer={singleLine ? lines[0][0] : ""}
            colSpan={columnCount - valueColSpan}
            footerStyle={{ textAlign: "right" }}
          />
          <Column footer={footerValue} colSpan={valueColSpan} />
        </Row>
      </ColumnGroup>
    );
  };

  // CLE-2798: what the card was actually charged on a PROCESSED credit-card payment — the plan/estimate
  // amount plus the surcharge. Shown as "amount + surcharge = charged" so the amount stays recognisable.
  const chargedAmountNote = (amountInCents: number, surchargeInCents: number) =>
    surchargeInCents > 0 ? (
      <span className="text-gray-600 font-normal text-base ml-2">
        + {parsePrice(centsToDollars(surchargeInCents))} surcharge ={" "}
        {parsePrice(centsToDollars(amountInCents + surchargeInCents))} charged
      </span>
    ) : null;

  const fullPaymentSurcharge = sortedProviderAmounts.reduce(
    (sum, row) => sum + (row.gfeCreditCardFeeAmountInCents || 0),
    0
  );
  const fullPaymentAmount = sortedProviderAmounts.reduce(
    (sum, row) => sum + (row.clearDiscountedPrice || 0),
    0
  );

  return (
    <>
      <div className="flex justify-content-between align-items-center w-full mt-3">
        <h3 style={{ marginTop: sizer(4) }}>
          Payment Full
          {fullPaymentSurcharge > 0 && (
            <span className="text-gray-600 font-normal text-base ml-2">
              Amount: {parsePrice(centsToDollars(fullPaymentAmount))}
              {chargedAmountNote(fullPaymentAmount, fullPaymentSurcharge)}
            </span>
          )}
        </h3>
        {orderStatus === orderStatusConstants.SENT_TO_PATIENT &&
          !edit &&
          actionHeader()}
      </div>
      <StyledDataTable
        rowGroupMode="subheader"
        style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
        groupRowsBy="providerName"
        value={sortedProviderAmounts}
        emptyMessage="No Provider Payout Calculations"
        footerColumnGroup={feeFooter(
          sortedProviderAmounts,
          fullPaymentColumns.length
        )}
      >
        {fullPaymentColumns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={col?.body}
          />
        ))}
      </StyledDataTable>
      {edit && actionFooter()}

      {sortedPayments.length > 0 && currentPage < sortedPayments.length && (
        <div style={{ marginBottom: sizer(4) }}>
          <h3 style={{ marginBottom: sizer(3), marginTop: sizer(3) }}>
            {currentPage === 0 ? firstPageLabel : `Installment ${currentPage}`}{" "}
            -{" "}
            {currentPage === 0 ||
            sortedPayments[currentPage].installmentInDb?.paymentId
              ? "Paid Date"
              : "Expected Payment Date"}
            :{parseDate(sortedPayments[currentPage].dateDue, false, true)} -
            Amount:
            {parsePrice(
              centsToDollars(sortedPayments[currentPage].amountInCents)
            )}
            {chargedAmountNote(
              sortedPayments[currentPage].amountInCents,
              sortedPayments[currentPage].gfeCreditCardFeeAmountInCents || 0
            )}
          </h3>
          <StyledDataTable
            rowGroupMode="subheader"
            style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
            groupRowsBy="providerName"
            value={sortedPayments[currentPage].providerBreakdown}
            emptyMessage="No Provider Breakdown"
            footerColumnGroup={feeFooter(
              sortedPayments[currentPage].providerBreakdown,
              installmentColumns.length
            )}
          >
            {installmentColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                body={col?.body}
              />
            ))}
          </StyledDataTable>
          {!isDownPaymentOnly && (
            <>
              <Paginator
                first={currentPage}
                rows={rows}
                totalRecords={payments.length}
                onPageChange={onPageChange}
              />
              <div
                style={{ borderBottom: "1px solid #ccc", marginTop: "8px" }}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ProviderPayoutCalculations;
